import { useEffect, useReducer, useRef } from 'react';
import { supabase } from '../services/supabase';

export interface LiveOrderItem {
  id?: string;
  product_id?: string;
  name: string;
  qty: number;
  quantity?: number;
  unit_price: number;
  notes?: string | null;
}

export interface LiveOrder {
  id: string;
  store_id: string;
  table_id: string | null;
  table_number: number | null;
  created_at: string;
  total_amount: number;
  status: string | null;
  state: string | null;
  payment_method: string | null;
  fiscal_status: string | null;
  is_order_ahead: boolean;
  pickup_time: string | null;
  items: LiveOrderItem[];
  customer_info: Record<string, unknown> | null;
}

type Action =
  | { type: 'set'; orders: LiveOrder[] }
  | { type: 'upsert'; order: LiveOrder }
  | { type: 'remove'; id: string };

function reducer(state: LiveOrder[], action: Action): LiveOrder[] {
  switch (action.type) {
    case 'set':
      return [...action.orders].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    case 'upsert': {
      const existing = state.find((o) => o.id === action.order.id);
      const merged = existing ? { ...existing, ...action.order } : action.order;
      const next = [merged, ...state.filter((o) => o.id !== action.order.id)];
      return next.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }
    case 'remove':
      return state.filter((o) => o.id !== action.id);
    default:
      return state;
  }
}

function normalizeRow(
  row: Record<string, unknown>,
  tableNumberLookup?: Map<string, number>,
): LiveOrder {
  const info = (row.customer_info as Record<string, unknown> | null) ?? null;
  const tableId = (row.table_id as string | null) ?? null;
  const items = (row.items as LiveOrderItem[] | null) ?? [];
  const tableNumber =
    (row.table_number as number | null) ??
    (tableId && tableNumberLookup ? tableNumberLookup.get(tableId) ?? null : null);

  return {
    id: row.id as string,
    store_id: row.store_id as string,
    table_id: tableId,
    table_number: tableNumber,
    created_at: (row.created_at as string) ?? new Date().toISOString(),
    total_amount: (row.total_amount as number) ?? 0,
    status: (row.status as string | null) ?? null,
    state: (row.state as string | null) ?? null,
    payment_method: (row.payment_method as string | null) ?? null,
    fiscal_status: (row.fiscal_status as string | null) ?? null,
    is_order_ahead: Boolean(
      info?.is_order_ahead === true ||
        info?.order_ahead === true ||
        row.is_order_ahead === true,
    ),
    pickup_time:
      (info?.pickup_time as string | null) ??
      (row.pickup_time as string | null) ??
      null,
    items: items.map((it) => ({ ...it, qty: it.qty ?? it.quantity ?? 1 })),
    customer_info: info,
  };
}

export interface UseLiveOrdersOptions {
  onInsert?: (order: LiveOrder) => void;
}

export function useLiveOrders(storeId: string, opts: UseLiveOrdersOptions = {}) {
  const [orders, dispatch] = useReducer(reducer, [] as LiveOrder[]);
  const loadingRef = useRef(true);
  const tableMapRef = useRef<Map<string, number>>(new Map());
  const onInsertRef = useRef(opts.onInsert);
  onInsertRef.current = opts.onInsert;

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;

    const init = async () => {
      // Build table lookup
      const { data: tables } = await supabase
        .from('restaurant_tables')
        .select('id, table_number')
        .eq('store_id', storeId);
      const map = new Map<string, number>();
      (tables ?? []).forEach((t) => {
        const tt = t as Record<string, unknown>;
        map.set(tt.id as string, tt.table_number as number);
      });
      tableMapRef.current = map;

      // Try guest_orders first, fallback to orders
      const guest = await supabase
        .from('guest_orders')
        .select('*')
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })
        .limit(100);

      let rows: Record<string, unknown>[] = (guest.data as Record<string, unknown>[] | null) ?? [];

      if (guest.error || rows.length === 0) {
        const ord = await supabase
          .from('orders')
          .select(
            'id, store_id, table_id, created_at, total_amount, status, payment_method, fiscal_status, items, customer_info',
          )
          .eq('store_id', storeId)
          .in('status', ['pending', 'open', 'confirmed', 'preparing', 'ready'])
          .order('created_at', { ascending: false })
          .limit(100);
        rows = (ord.data as Record<string, unknown>[] | null) ?? [];
      }

      if (cancelled) return;
      dispatch({
        type: 'set',
        orders: rows.map((r) => normalizeRow(r, map)),
      });
      loadingRef.current = false;
    };

    void init();

    const channel = supabase
      .channel(`staff-live-orders-${storeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'guest_orders',
          filter: `store_id=eq.${storeId}`,
        },
        (payload) => {
          const evt = payload.eventType;
          if (evt === 'DELETE') {
            const id = (payload.old as Record<string, unknown> | null)?.id as string | undefined;
            if (id) dispatch({ type: 'remove', id });
            return;
          }
          const row = payload.new as Record<string, unknown>;
          const order = normalizeRow(row, tableMapRef.current);
          dispatch({ type: 'upsert', order });
          if (evt === 'INSERT') onInsertRef.current?.(order);
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `store_id=eq.${storeId}`,
        },
        (payload) => {
          const evt = payload.eventType;
          if (evt === 'DELETE') {
            const id = (payload.old as Record<string, unknown> | null)?.id as string | undefined;
            if (id) dispatch({ type: 'remove', id });
            return;
          }
          const row = payload.new as Record<string, unknown>;
          const order = normalizeRow(row, tableMapRef.current);
          dispatch({ type: 'upsert', order });
          if (evt === 'INSERT') onInsertRef.current?.(order);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [storeId]);

  return { orders, loading: loadingRef.current };
}
